import { useQuery } from '@apollo/client/react';
import {
  DimeButton,
  DimeCard,
  DimeCardAction,
  DimeCardContent,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeFieldError,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { PlugZap } from 'lucide-react';
import { useState } from 'react';

import { ListEmptyState } from '../../../components/ListEmptyState.js';
import { ListErrorState } from '../../../components/ListErrorState.js';
import { useDeniedTooltip } from '../../../lib/access/index.js';
import { usePermissions } from '../../../modules/iam/permissions-context.js';
import { ConnectSageIntacctDialog } from '../../../modules/sage-intacct/connect-sage-intacct-dialog.js';
import { GET_SAGE_INTACCT_CONNECTION } from '../../../modules/sage-intacct/operations.js';
import { SageIntacctConnectionDetails } from '../../../modules/sage-intacct/sage-intacct-connection-details.js';
import { SageIntacctSyncedLists } from '../../../modules/sage-intacct/sage-intacct-synced-lists.js';
import { useSyncSageIntacctNow } from '../../../modules/sage-intacct/use-sync-sage-intacct-now.js';

export const Route = createFileRoute('/integrations/sage-intacct/')({
  component: SageIntacctPage,
});

const SYNC_POLL_INTERVAL_MS = 5000;

// eslint-disable-next-line react-refresh/only-export-components
function SageIntacctPage() {
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('accountingIntegration:edit');
  const deniedTooltip = useDeniedTooltip();
  const editDeniedTooltip = canEdit ? undefined : deniedTooltip('accountingIntegration:edit');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pollInterval, setPollInterval] = useState(0);
  const { data, loading, error, refetch } = useQuery(GET_SAGE_INTACCT_CONNECTION, {
    pollInterval,
  });
  const connection = data?.sageIntacctConnections.edges[0]?.node ?? null;
  const dateLastSynced = connection?.dateLastSynced ?? null;
  const { syncNow, awaitSync, syncing, error: syncError } = useSyncSageIntacctNow(dateLastSynced);

  const nextPollInterval = syncing ? SYNC_POLL_INTERVAL_MS : 0;
  if (pollInterval !== nextPollInterval) setPollInterval(nextPollInterval);

  const actions = connection ? (
    <div className="flex gap-2">
      <DimeButton
        type="button"
        variant="outline"
        disabled={!canEdit}
        disabledTooltip={editDeniedTooltip}
        onClick={() => setDialogOpen(true)}
      >
        Update credentials
      </DimeButton>
      <DimeButton
        type="button"
        disabled={syncing || !canEdit}
        disabledTooltip={editDeniedTooltip}
        onClick={() => void syncNow()}
      >
        {syncing ? 'Syncing…' : 'Sync now'}
      </DimeButton>
    </div>
  ) : null;

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base">
          <h1>Sage Intacct</h1>
        </DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Nudool posts to one Sage company at the top level, naming the location on each
          transaction, and copies Sage&apos;s accounts, locations and tax details every day.
        </DimeCardDescription>
        <DimeCardAction>{actions}</DimeCardAction>
      </DimeCardHeader>
      <DimeCardContent className="flex flex-col gap-6 px-0">
        {error ? (
          <ListErrorState
            title="Could not load the Sage Intacct connection"
            message={error.message}
            onRetry={() => void refetch()}
          />
        ) : loading && !data ? null : connection ? (
          <>
            {syncError ? <DimeFieldError>{syncError}</DimeFieldError> : null}
            <SageIntacctConnectionDetails connection={connection} />
            <SageIntacctSyncedLists dateLastSynced={dateLastSynced} />
          </>
        ) : (
          <ListEmptyState
            icon={<PlugZap />}
            title="Sage Intacct isn't connected yet"
            description="Connect the Sage web services user to copy its accounts, locations and tax details into Nudool."
            action={
              <DimeButton
                type="button"
                disabled={!canEdit}
                disabledTooltip={editDeniedTooltip}
                onClick={() => setDialogOpen(true)}
              >
                Connect Sage Intacct
              </DimeButton>
            }
          />
        )}
      </DimeCardContent>
      <ConnectSageIntacctDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onConnected={awaitSync}
        current={connection}
      />
    </DimeCard>
  );
}
